import { Link } from "react-router-dom";
import {
  ArrowRight,
  Activity,
  Globe,
  GitCompareArrows,
  Newspaper,
  Sparkles,
  ShieldCheck,
  Cpu,
  Database,
  Zap,
} from "lucide-react";
import { SectionHeading } from "../components/SectionHeading";
import { FeatureCard } from "../components/FeatureCard";
import { EmailCapture } from "../components/EmailCapture";
import { AdSlot } from "../components/ads/AdSlot";
import { RecommendedResources } from "../components/RecommendedResources";
import { SponsorBlock } from "../components/SponsorBlock";
import { LinkInBio } from "../components/LinkInBio";

const HERO_BG =
  "https://images.unsplash.com/photo-1679391029864-d46f366a456b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxzb2NjZXIlMjBzdGFkaXVtJTIwbGlnaHRzfGVufDB8fHx8MTc4MTAzMzA4MHww&ixlib=rb-4.1.0&q=85";
const NETWORK_BG =
  "https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRpZ2l0YWwlMjBuZXR3b3JrJTIwbm9kZXN8ZW58MHx8fHwxNzgxMDMzMDgwfDA&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  {
    icon: Activity,
    title: "Free Match Analyzer",
    description: "AI-assisted breakdowns of any fixture — form, momentum and context, explained in plain language.",
    status: "soon",
    testId: "feature-match-analyzer",
  },
  {
    icon: Globe,
    title: "2026 World Cup Hub",
    description: "Live group standings now — your intelligence center for the tournament.",
    status: "live",
    to: "/standings",
    testId: "feature-worldcup-hub",
  },
  {
    icon: GitCompareArrows,
    title: "Team Comparison",
    description: "Put two sides head-to-head across the metrics that actually move matches.",
    status: "soon",
    testId: "feature-team-comparison",
  },
  {
    icon: Newspaper,
    title: "AI Daily Digest",
    description: "A concise, AI-curated football briefing delivered to your inbox every morning.",
    status: "soon",
    testId: "feature-daily-digest",
  },
];

const TRUST = [
  { icon: Cpu, label: "AI-Assisted", sub: "Analysis you can read" },
  { icon: ShieldCheck, label: "Independent", sub: "No betting, no bias" },
  { icon: Database, label: "Multi-Sport Ready", sub: "Built to scale" },
  { icon: Zap, label: "Always Free", sub: "Core analysis, on us" },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* ---------------- HERO ---------------- */}
      <section className="relative overflow-hidden" data-testid="hero">
        <div className="absolute inset-0">
          <img
            src={HERO_BG}
            alt=""
            className="h-full w-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-bg via-brand-bg/85 to-brand-bg/60" />
          <div className="bg-grid pointer-events-none absolute inset-0 opacity-40" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 py-24 sm:px-6 sm:py-32 lg:px-8 lg:py-40">
          <div className="max-w-3xl animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-royal/40 bg-brand-navy/40 px-4 py-1.5 font-mono-data text-xs font-medium uppercase tracking-[0.2em] text-brand-cyan">
              <Sparkles className="h-3.5 w-3.5" />
              Independent AI Sports Intelligence
            </span>

            <h1 className="mt-6 font-display text-5xl font-black leading-[1.05] tracking-tighter text-white sm:text-6xl lg:text-7xl">
              Understand the game with{" "}
              <span className="text-gradient-brand">AI First</span>.
            </h1>

            <p className="mt-6 max-w-xl text-lg leading-relaxed text-brand-silver">
              Free, AI-assisted football analysis and 2026 World Cup intelligence —
              built for fans, creators and analysts. No betting tips. No noise.
              Just clear, data-driven insight.
            </p>

            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/sports/football"
                data-testid="hero-primary-cta"
                className="group inline-flex items-center justify-center gap-2 rounded-lg bg-brand-royal px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-brand-cyan hover:text-brand-bg"
              >
                Explore Football
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <a
                href="#newsletter"
                data-testid="hero-secondary-cta"
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-brand-navy bg-brand-navy/30 px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:border-brand-cyan/60"
              >
                Get Free Insights
              </a>
            </div>

            {/* Trust strip */}
            <div className="mt-14 grid grid-cols-2 gap-4 sm:grid-cols-4">
              {TRUST.map((t) => (
                <div key={t.label} className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-brand-navy bg-brand-bg/60 text-brand-cyan">
                    <t.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">{t.label}</p>
                    <p className="font-mono-data text-[10px] uppercase tracking-wider text-brand-silver">
                      {t.sub}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- HEADER AD SLOT ---------------- */}
      <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <AdSlot variant="header" />
      </section>

      {/* ---------------- FEATURES ---------------- */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24" data-testid="features-section">
        <SectionHeading
          overline="What you'll get"
          title="An AI sports media platform, not a stats dump"
          subtitle="Phase 1 sets the foundation. These modules go live as AI First Sports grows — starting with football."
        />
        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((f) => (
            <FeatureCard key={f.testId} {...f} to={f.to || "/sports/football"} />
          ))}
        </div>
      </section>

      {/* ---------------- NEWSLETTER / EMAIL CAPTURE ---------------- */}
      <section id="newsletter" className="relative overflow-hidden py-20" data-testid="newsletter-section">
        <img src={NETWORK_BG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-brand-bg/85" />
        <div className="relative z-10 mx-auto max-w-3xl px-4 text-center sm:px-6">
          <div className="rounded-2xl border border-brand-cyan/20 bg-brand-navy/40 p-8 sm:p-12 glow-blue">
            <span className="font-mono-data text-xs font-semibold uppercase tracking-[0.25em] text-brand-cyan">
              AI Daily Digest
            </span>
            <h2 className="mt-3 font-display text-3xl font-bold text-white sm:text-4xl">
              Get the match intelligence brief
            </h2>
            <p className="mx-auto mt-3 max-w-lg text-base leading-relaxed text-brand-silver">
              Join the list for free AI-assisted football insights and early
              access as we expand into new sports.
            </p>
            <div className="mx-auto mt-8 max-w-md">
              <EmailCapture source="homepage-newsletter" />
              <p className="mt-3 font-mono-data text-[10px] uppercase tracking-wider text-brand-silver/60">
                No spam · Unsubscribe anytime
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- RECOMMENDED RESOURCES + SIDEBAR AD ---------------- */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8" data-testid="resources-section">
        <SectionHeading
          overline="Curated for you"
          title="Recommended sports resources"
          subtitle="Hand-picked tools and reads. Sponsored and affiliate items are always clearly labelled."
        />
        <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <RecommendedResources />
            <div className="mt-8">
              <AdSlot variant="in-article" />
            </div>
          </div>
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24">
              <AdSlot variant="sidebar" />
            </div>
          </aside>
        </div>
      </section>

      {/* ---------------- SPONSOR BLOCK ---------------- */}
      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <SponsorBlock />
      </section>

      {/* ---------------- LINK IN BIO ---------------- */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24" data-testid="linkinbio-section">
        <SectionHeading
          align="center"
          overline="One link, everything"
          title="Follow AI First Sports"
          subtitle="Everything we publish, in one place."
        />
        <div className="mt-10">
          <LinkInBio />
        </div>
      </section>

      {/* ---------------- FOOTER AD ---------------- */}
      <section className="mx-auto max-w-7xl px-4 pb-12 sm:px-6 lg:px-8">
        <AdSlot variant="footer" />
      </section>
    </div>
  );
}
